import { useState } from 'react';
import { useTranslation } from '@/i18n';
import { showApiError } from '@/errors/showApiError';
import { useToastStore } from '@/store/useToastStore';
import { downloadExcelExport, uploadExcelImport, type ExcelEntity } from '@/utils/excelIo';
import type { ImportResult } from '@/types/excelImport';

interface UseExcelIo {
  exporting: boolean;
  importing: boolean;
  /**
   * Downloads the round-trip workbook, narrowed to `ids` when given.
   * Pass the IDs the list is currently showing so the file matches the screen.
   */
  exportExcel: (ids?: readonly string[]) => Promise<void>;
  /** Dry run — returns the preview report without writing anything. */
  previewImport: (file: File) => Promise<ImportResult | null>;
  /**
   * Applies the workbook and returns the final report. `excludeRows` holds the
   * `"{sheet}:{row}"` tokens of rows deselected in the preview.
   */
  commitImport: (file: File, excludeRows?: readonly string[]) => Promise<ImportResult | null>;
}

/**
 * Export/import plumbing for one domain's list page. Mirrors `useReportDownload`:
 * the hook owns the busy flags and the user feedback, the caller owns the UI.
 */
export function useExcelIo(entity: ExcelEntity): UseExcelIo {
  const { t } = useTranslation('common');
  const { addToast } = useToastStore();
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);

  const exportExcel = async (ids?: readonly string[]) => {
    setExporting(true);
    try {
      await downloadExcelExport(entity, ids);
    } catch (error) {
      // An error response to a blob request arrives as a Blob, so the contract body has
      // to be unwrapped before the shared error handler can read the code.
      showApiError(await unwrapBlobError(error));
    } finally {
      setExporting(false);
    }
  };

  const runImport = async (
    file: File,
    dryRun: boolean,
    excludeRows?: readonly string[],
  ): Promise<ImportResult | null> => {
    setImporting(true);
    try {
      const result = await uploadExcelImport(entity, file, dryRun, excludeRows);

      if (!dryRun) {
        addToast(
          t('common:excelImport.messages.imported', {
            created: result.created,
            updated: result.updated,
          }),
          'success',
        );
      }

      return result;
    } catch (error) {
      showApiError(error);
      return null;
    } finally {
      setImporting(false);
    }
  };

  return {
    exporting,
    importing,
    exportExcel,
    previewImport: (file) => runImport(file, true),
    commitImport: (file, excludeRows) => runImport(file, false, excludeRows),
  };
}

/**
 * Replaces the Blob body of a failed blob request with its parsed JSON, so
 * `parseAxiosError` sees the usual `{ code, detail, … }` contract.
 */
async function unwrapBlobError(error: unknown): Promise<unknown> {
  const response = (error as { response?: { data?: unknown } })?.response;

  if (!(response?.data instanceof Blob)) {
    return error;
  }

  try {
    response.data = JSON.parse(await response.data.text());
  } catch {
    // Not JSON (a proxy error page, say) — leave it; parseAxiosError falls back to status.
    response.data = undefined;
  }

  return error;
}
